import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, LogIn, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { translateApiError } from '@/lib/errorMessages';

export default function Login() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (window._workplus_session_expired) {
      setError(t('login.sessionExpired'));
      window._workplus_session_expired = false;
    }
    if (window._workplus_rate_limit_msg) {
      setError(window._workplus_rate_limit_msg);
      window._workplus_rate_limit_msg = null;
    }
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(translateApiError(err, t, t('login.loginFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-wp-darkest" data-testid="login-page">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher variant="auth" />
      </div>

      {/* Left side - Animated Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden auth-hero">
        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-wp-medium/30 rounded-full blur-[100px] animate-float" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-wp-light/20 rounded-full blur-[120px] animate-float" style={{ animationDelay: '-3s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-wp-dark/30 rounded-full blur-[80px] animate-float" style={{ animationDelay: '-1.5s' }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-16 text-white">
          <div className="flex items-center mb-12">
            <span className="text-5xl font-light tracking-wide text-white font-heading">WORK</span>
            <span className="text-5xl font-light tracking-wide text-wp-light font-heading">+</span>
          </div>
          
          <h1 className="text-5xl font-bold mb-6 font-heading leading-tight">
            {t('login.presenceBased')}<br />
            <span className="gradient-text">{t('login.timeTracking')}</span>
          </h1>
          
          <p className="text-xl text-wp-light/60 max-w-md leading-relaxed">
            {t('login.heroDescription')}
          </p>

          {/* Stats preview */}
          <div className="mt-16 grid grid-cols-3 gap-6">
            <div className="glass-card rounded-xl p-4">
              <p className="text-3xl font-bold font-heading text-emerald-400">98%</p>
              <p className="text-sm text-wp-light/50 mt-1">{t('login.accuracy')}</p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-3xl font-bold font-heading text-wp-lightest">24/7</p>
              <p className="text-sm text-wp-light/50 mt-1">{t('login.monitoring')}</p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-3xl font-bold font-heading text-wp-light">5min</p>
              <p className="text-sm text-wp-light/50 mt-1">{t('login.setup')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative">
        {/* Subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-wp-darker/50 to-transparent" />
        
        <div className="relative z-10 w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center mb-10 lg:hidden">
            <span className="text-3xl font-light tracking-wide text-white font-heading">WORK</span>
            <span className="text-3xl font-light tracking-wide text-wp-light font-heading">+</span>
          </div>

          <div className="glass-card rounded-2xl p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white font-heading">{t('login.welcomeBack')}</h2>
              <p className="text-wp-light/50 mt-2">{t('login.enterCredentials')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 text-sm text-rose-400 bg-rose-500/10 rounded-xl border border-rose-500/20" data-testid="login-error">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-wp-light/70">{t('login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-wp-light/30 focus:border-wp-medium"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-wp-light/70">{t('login.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-wp-light/30 focus:border-wp-medium pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-wp-light/50 hover:text-wp-lightest transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-wp-medium hover:bg-wp-light text-white font-medium rounded-xl btn-glow" 
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <LogIn className="h-5 w-5 mr-2" />
                    {t('login.signIn')}
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 text-center space-y-2">
              <div>
                <Link to="/forgot-password" className="text-wp-light/50 hover:text-wp-lightest text-sm transition-colors">
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <div>
                <span className="text-wp-light/40">{t('login.noAccount')} </span>
                <Link to="/register-company" className="text-wp-lightest hover:text-white font-medium transition-colors" data-testid="register-company-link">
                  {t('login.registerCompany')}
                </Link>
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
}
